import { Alert, Button, Stack, TextField } from '@mui/material';

import { useNavigateParams } from '../../../hooks/useNavigateParams';
import SystemTabs from '../../../ui/SystemTabs';
import Checkbox from '../../form/Checkbox';
import Input from '../../form/Input';
import SelectEmpresa from '../../form/SelectEmpresa';

import useFiscalRegraModule from './modules/useFiscalRegraModule';
import TabRegras from './tabs/TabRegras';

function Form({
    dados,
    setDados,
    handleSubmit,
    caminhoVoltar,
    OptionsEmpresa,
    editando,
    carregando,
    falhaCarregamento,
    recarregarPerfil,
}) {
    const navigate = useNavigateParams();

    const regraModule = useFiscalRegraModule({
        perfil: dados,
        recarregarPerfil,
    });

    function handleChange(event) {
        const { name, value, type, checked } = event.target;

        setDados((anterior) => {
            const valor = type === 'checkbox' ? checked : value;

            const proximo = {
                ...anterior,
                [name]: name === 'codigo' ? String(valor).toUpperCase() : valor,
            };

            if (name === 'ativo' && !checked) {
                proximo.integracaoAtivo = false;
            }

            if (name === 'revisaoPendente' && checked) {
                proximo.integracaoAtivo = false;
            }

            if (name === 'integracaoAtivo' && checked) {
                proximo.ativo = true;
                proximo.revisaoPendente = false;
            }

            return proximo;
        });
    }

    function submit(event) {
        event.preventDefault();
        handleSubmit(dados);
    }

    const regras = Array.isArray(dados?.Regras) ? dados.Regras : [];

    const regrasPublicadas = regras.filter((regra) => regra.ativo && regra.integracaoAtivo && !regra.revisaoPendente).length;

    const podePublicar = editando && regrasPublicadas > 0;

    const tabPerfil = (
        <>
            <div>
                <SelectEmpresa
                    name="empresaId"
                    text="Empresa"
                    options={OptionsEmpresa}
                    handleOnChange={handleChange}
                    value={dados?.empresaId || ''}
                    disabled={editando}
                    required
                    notOptNull={false}
                />

                <Input
                    text="Código"
                    type="text"
                    name="codigo"
                    placeholder="Ex.: PADARIA"
                    title="Código interno do perfil fiscal"
                    handleOnChange={handleChange}
                    value={dados?.codigo || ''}
                    maxlength={50}
                    required
                />

                <Input
                    text="Descrição"
                    type="text"
                    name="descricao"
                    placeholder="Descrição do perfil fiscal"
                    title="Descrição para identificação do perfil"
                    inputWidth="350px"
                    handleOnChange={handleChange}
                    value={dados?.descricao || ''}
                    maxlength={150}
                    required
                />
            </div>

            <div>
                <TextField
                    label="Origem do cadastro"
                    value={dados?.origemCadastro || 'MANUAL'}
                    size="small"
                    disabled
                    sx={{ width: 220 }}
                />

                <TextField label="Versão" value={dados?.versao || 1} size="small" disabled sx={{ width: 100 }} />

                <TextField
                    label="Produtos vinculados"
                    value={Number(dados?.produtosVinculados) || 0}
                    size="small"
                    disabled
                    sx={{ width: 180 }}
                />
            </div>

            <div>
                <Checkbox
                    title="Indica que o perfil pode ser utilizado internamente"
                    text="Ativo"
                    name="ativo"
                    handleOnChange={handleChange}
                    value={dados?.ativo}
                    checked={Boolean(dados?.ativo)}
                />

                <Checkbox
                    title="Indica que o perfil ainda precisa ser revisado"
                    text="Revisão pendente"
                    name="revisaoPendente"
                    handleOnChange={handleChange}
                    value={dados?.revisaoPendente}
                    checked={Boolean(dados?.revisaoPendente)}
                />

                <Checkbox
                    title="Publica este perfil na API de integração"
                    text="Publicado na API"
                    name="integracaoAtivo"
                    handleOnChange={handleChange}
                    value={dados?.integracaoAtivo}
                    checked={Boolean(dados?.integracaoAtivo)}
                    disabled={!podePublicar && !dados?.integracaoAtivo}
                />

                <Checkbox
                    title="Permite compartilhar o perfil com todas as empresas"
                    text="Todas as empresas"
                    name="integracaoTodasEmpresas"
                    handleOnChange={handleChange}
                    value={dados?.integracaoTodasEmpresas}
                    checked={Boolean(dados?.integracaoTodasEmpresas)}
                />
            </div>

            {!editando && (
                <Alert severity="info" sx={{ my: 2 }}>
                    Cadastre primeiro o perfil como rascunho. Depois inclua pelo menos uma regra fiscal antes de publicá-lo na
                    API.
                </Alert>
            )}

            {editando && regrasPublicadas === 0 && (
                <Alert severity="warning" sx={{ my: 2 }}>
                    O perfil ainda não possui uma regra revisada e publicada.
                </Alert>
            )}

            <TextField
                label="Observação"
                name="observacao"
                value={dados?.observacao || ''}
                onChange={handleChange}
                multiline
                minRows={3}
                fullWidth
                sx={{ my: 2 }}
            />
        </>
    );

    const tabRegras = !editando ? (
        <Alert severity="info">Salve o perfil para liberar o cadastro das regras fiscais.</Alert>
    ) : (
        <TabRegras regras={regras} module={regraModule} />
    );

    const tabsConfig = [
        {
            key: 'perfil',
            label: 'Perfil fiscal',
            title: 'Dados do perfil fiscal',
            panel: tabPerfil,
        },
        {
            key: 'regras',
            label: `Regras (${regras.length})`,
            title: 'Regras fiscais do perfil',
            panel: tabRegras,
        },
    ];

    return (
        <form onSubmit={submit} autoComplete="off">
            <h1>{editando ? 'EDIÇÃO DO PERFIL FISCAL' : 'CADASTRO DO PERFIL FISCAL'}</h1>

            <SystemTabs tabs={tabsConfig} initialKey="perfil" />

            <Stack direction="row" spacing={1} sx={{ mt: 2 }}>
                <Button variant="contained" color="secondary" type="button" onClick={() => navigate(caminhoVoltar)}>
                    Voltar
                </Button>

                <Button type="submit" variant="contained" color="success" disabled={carregando || falhaCarregamento}>
                    {editando ? 'Atualizar' : 'Cadastrar'}
                </Button>
            </Stack>
        </form>
    );
}

export default Form;
